import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import React, { useEffect, useState } from 'react';
import FormControl from '@mui/material/FormControl';
import Chip from '@mui/material/Chip';
import constants from '@shared-styles/constants.module.scss';

/**
 * @typedef {object} MuiMultiSelectionDropDownOption
 * @property {string} label
 * @property {number|string} value
 * @property {boolean} [disabled]
 */

/**
 * @typedef {object} MuiMultiSelectionDropDownOwnProps
 * @property {string} [inputLabel]
 * @property {(newValue: Array<MuiMultiSelectionDropDownOption>) => void} [setSelectedValue]
 * @property {Array<MuiMultiSelectionDropDownOption>} [options]
 * @property {Array<MuiMultiSelectionDropDownOption>} [selectedValues]
 * @property {boolean} [error]
 */

/**
 * @typedef {MuiMultiSelectionDropDownOwnProps &
 *   Omit<React.ComponentProps<typeof Autocomplete>, 'renderInput' | 'options'>} MuiMultiSelectionDropDownProps
 */

/**
 * @param {MuiMultiSelectionDropDownProps} props
 */
const MuiMultiSelectionDropDown = props => {
  const { inputLabel, setSelectedValue, options, selectedValues, limitTags, error, ...otherProps } =
    props;

  const [value, setValue] = useState([]);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (!selectedValues) {
      return;
    }
    setValue(selectedValues);
  }, [selectedValues]);

  const handleChange = newValue => {
    setValue(newValue);
    setSelectedValue(newValue);
  };

  return (
    <div className='mui-multiselect-dropdown'>
      <FormControl variant='standard'>
        <Autocomplete
          {...otherProps}
          value={value}
          multiple
          disableCloseOnSelect
          getOptionDisabled={option => option?.disabled}
          options={options}
          getOptionLabel={option => option.label}
          onChange={(_, newValue) => {
            handleChange(newValue);
          }}
          onInputChange={(_, newInputValue) => {
            setInputValue(newInputValue);
          }}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          inputValue={inputValue}
          slotProps={{
            paper: {
              sx: {
                borderRadius: '15px',
                backgroundColor: constants.inputBackground,
                width: '98%',
                marginLeft: 'auto',
                marginRight: 'auto',
                marginTop: '-2px',
                '& .MuiAutocomplete-listbox': {
                  '& .MuiAutocomplete-option[aria-selected="true"]': {
                    backgroundColor: constants.borderColor
                  },
                  '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
                    backgroundColor: '#e0e0e0'
                  }
                }
              }
            }
          }}
          renderValue={(value, getTagProps) => {
            const numTags = value.length;

            return (
              <>
                {value.slice(0, limitTags).map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={index}
                    variant='outlined'
                    label={option.displayName}
                  />
                ))}

                {numTags > limitTags && ` +${numTags - limitTags}`}
              </>
            );
          }}
          renderInput={params => (
            <TextField
              {...params}
              variant='standard'
              error={error}
              placeholder={value.length == 0 && inputValue.length == 0 ? inputLabel : ''}
              slotProps={{
                input: { ...params.InputProps, disableUnderline: true }
              }}
            />
          )}
        />
      </FormControl>
    </div>
  );
};

export default MuiMultiSelectionDropDown;
